import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { setTheme } from 'ngx-bootstrap/utils';
import { Subscription } from 'rxjs';
import Intercom from '@intercom/messenger-js-sdk';
import { ApplicationContext } from './models';
import { ApplicationContextService } from './services/application-context.service';
import { AuthService } from './services/auth.service';
import { CacheService } from './services/cache.service';
import { DrawerService } from './shared/services/drawer.service';
import { GlobalSpinnerService } from './shared/services/global-spinner.service';
import { EnvironmentService } from './core/services/environment/environment.service';
import { DataService } from './core/services/data.service';
import { AuthSessionIntegrityService } from './core/services/auth-session-integrity.service';
import { ThemeService } from './core/services/theme/theme.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy, OnInit { 
  title = 'admin-app';

  isLoading: boolean = true;

  private readonly _router = inject(Router);
  private readonly _modalService = inject(NgbModal); 
  private readonly _drawerService = inject(DrawerService);
  private readonly _titleService = inject(Title);
  private readonly _authService = inject(AuthService);
  private readonly _environmentService = inject(EnvironmentService);

  private readonly _routerEventsSubscription: Subscription;

  private readonly _contextChangeSubscription: Subscription;
  private readonly _contextInitializedSubscription: Subscription;

  private _intercomInitializedUserId: string | null = null;

  // The page (URL path) of the last navigation, to tell a navigation that changes only the query or fragment.
  private _lastPagePath: string | null = null;

  protected faviconUrl: string = '/assets/images/favicon_loda.ico';

  protected overrideCssUrl: string = null;

  protected context: ApplicationContext;

  private readonly _dataService = inject(DataService);
  private readonly _globalSpinner = inject(GlobalSpinnerService);
  // Injected only to start it: ThemeService owns the page theme from the first render, on every route.
  private readonly _themeService = inject(ThemeService);

  protected readonly globalSpinnerVisible$ = this._globalSpinner.visible$;

  constructor(private readonly _applicationContextService: ApplicationContextService,
    private readonly _cacheService: CacheService,
    private window: Window,
    private readonly _authSessionIntegrityService: AuthSessionIntegrityService
  ) {

     setTheme('bs5');

    this._contextInitializedSubscription = this._applicationContextService.initialized.subscribe(context => {
      this.context = context;
      this.setCssOverrideFromContext(context);
      this.initializeIntercom();
    });

    this._contextChangeSubscription = this._applicationContextService.changes.subscribe(context => {
      this.context = context;
      this.setCssOverrideFromContext(context);
      this.initializeIntercom();
    });

    this._routerEventsSubscription = this._router.events.subscribe((routerEvent) => {
      this.handleRouterEvent(routerEvent);
    });
  }

  ngOnInit(): void {
    // Initialize tenant prefix at the beginning of the app lifecycle
    this._cacheService.initializeTenantPrefix();

    // Start watching for cross-tab identity changes (e.g. impersonation in another tab swapping the
    // shared localStorage JWT), which would otherwise cause this tab to write under the wrong identity.
    this._authSessionIntegrityService.start();

    // Load the CSS override for all routes (including login/register)
    this.loadCssOverride();
  }

  ngOnDestroy(): void {
    this._routerEventsSubscription?.unsubscribe();
    this._contextChangeSubscription?.unsubscribe();
    this._contextInitializedSubscription?.unsubscribe();
  }

  private loadCssOverride(): void {
    // If we already have context (user is logged in), use it
    if (this.context?.globalConfig?.company && this.context?.userPermissions?.companyId) {
      this.setCssOverrideFromContext(this.context);
      return;
    }

    // For unauthenticated pages (login, register, etc.), fetch company GUID
    // Use getWithoutAuth to bypass the auth interceptor (no token on login page)
    this._dataService.getWithoutAuth(`api/Admin/company/TPOUserRegistrationURL`).subscribe({
      next: (res) => {
        const companyGuid = res?.companyGuid || res?.companyGUID;
        if (companyGuid) {
          this.overrideCssUrl =
            this._environmentService.apiInfo.publicApiBaseUrl +
            `api/PublicApi/custom-css/${companyGuid}/Admin`;
        }
      },
      error: () => {
        // Silently fail - CSS override is non-critical
      }
    });
  }

  private setCssOverrideFromContext(context: ApplicationContext): void {
    if (!context) return;
    const company = context.globalConfig?.company?.find(
      (c) => c.companyId == context.userPermissions?.companyId
    );
    if (company?.companyGUID) {
      this.overrideCssUrl =
        this._environmentService.apiInfo.publicApiBaseUrl +
        `api/PublicApi/custom-css/${company.companyGUID}/Admin`;
    }
  }

  private initializeIntercom(): void {
    if (this.context == null) {
      return;
    }

    const user = this.context.currentlyLoggedInUser;
    const profile = this.context.currentlyLoggedInUserProfile;
    const userProfile = profile?.userProfile;

    if (user == null || profile == null || userProfile == null) {
      return;
    }

    // const isAllowedCompany = this.context.isCompanyLodasoft; // to be replaced with a feature flag in super admin
    const isAllowedCompany = false;

    const env = this._environmentService.environment;
    const isDevEnv = env === 'loda-dv' || env === 'Local' || env === 'loda-local';

    if (!isDevEnv && !isAllowedCompany) {
      return;
    }

    if (this._intercomInitializedUserId === user.userId) {
      return;
    }

    const nameParts = [userProfile.firstName, userProfile.lastName]
      .filter((part) => part?.trim())
      .join(' ');

    const createdAt = this.toUnixSeconds(userProfile.inviteDate ?? userProfile.confirmationDate);
    const intercomCompanyName = this.context.currentCompany?.companyName
      ?? userProfile.companyModel?.companyName
      ?? 'No Company Provided';

    this._authService.getIntercomUserToken().subscribe({
      next: (response) => {
        if (response?.token == null) {
          return;
        }

        Intercom({
          app_id: 'ar9lpvlb',
          intercom_user_jwt: response.token,
          user_id: user.userId,
          name: nameParts || profile.userName,
          email: userProfile.email ?? user.email,
          created_at: createdAt ?? undefined,
          hide_default_launcher: true,
          company: {
            company_id: userProfile.companyId,
            name: intercomCompanyName,
          },
        });

        this._intercomInitializedUserId = user.userId;
      },
      error: () => {
      }
    });
  }

  private toUnixSeconds(dateValue: string | null | undefined): number | undefined {
    if (dateValue == null) {
      return undefined;
    }

    const parsed = Date.parse(dateValue);
    if (Number.isNaN(parsed)) {
      return undefined;
    }

    return Math.floor(parsed / 1000);
  }

  private setPageTitle(hostName: string = null) {

    let title = "Lodasoft - CMA Platform";
    if (hostName && hostName.toLowerCase().includes('leo.prmg.net')) {
      title = 'LEO - Loan Express Originator';
    }

    if (this.context) {
      const company = this.context.globalConfig?.company?.find(c => c.companyGUID == this.context.currentlyLoggedInUser.companyGuid);

      if (this.context.isTpo && this.context?.tpoConfig?.browserTabTitleText) {
        title = this.context?.tpoConfig?.browserTabTitleText;
      } else if (company?.opsPortalBrowserTabTitleText) {
        title = company.opsPortalBrowserTabTitleText
      }
    }
    this._titleService.setTitle(title);
  }

  private handleRouterEvent(routerEvent): void {
    if (routerEvent instanceof NavigationEnd) {
      // A navigation that stays on the page (only the query or fragment changes, or the same URL reloads) runs no
      // route guards, so drawers holding unsaved work were never asked; they stay open, as the page did.
      const pagePath = (routerEvent.urlAfterRedirects ?? routerEvent.url).split(/[?#]/)[0];
      this._drawerService.hideAll(true, pagePath === this._lastPagePath);
      this._lastPagePath = pagePath;

      this._modalService.dismissAll();

      if (routerEvent.url) {
        if (!routerEvent.url.match(/^\/admin\/app-details/) && !routerEvent.url.match(/^\/tpo\/app-details/)) {
          const hostname = this.window.location.hostname;
          this.setPageTitle(hostname);
          this._applicationContextService.resetLoanInfo();
        }

        if (routerEvent.url.match(/^\/#/)) {
          this.handleOldHashRoutesForBackwardCompatibility(routerEvent.url);
        }
      }
      this.isLoading = false;
    }

    if (
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.isLoading = false;
    }
  }

  private handleOldHashRoutesForBackwardCompatibility = (url: string) => {
    if (url.match(/^\/#\/appdetail/)) {
      this.handleAppDetailHashRouteForBackwardCompatibility(url);
      return;
    } else if (url.match(/^\/#\/events/)) {
      const urlToRedirectTo = url.replace('/#/events', '/admin/calendar');
      this._router.navigateByUrl(urlToRedirectTo);
      return;
    }
    let urlToRedirectTo = url.replace('/#', '');
    urlToRedirectTo = this.properlyEncodeUrl(urlToRedirectTo);
    this._router.navigateByUrl(urlToRedirectTo);
  };

  private handleAppDetailHashRouteForBackwardCompatibility = (url: string) => {
    const segments = url.split('/');
    const indexForAppDetailSegment = segments.findIndex(
      (s) => s.toLocaleLowerCase() === 'appdetail'
    );
    if (indexForAppDetailSegment >= 0) {
      const indexOfAppIdSegment = indexForAppDetailSegment + 2;

      if (indexForAppDetailSegment <= segments.length - 1) {
        const appId = segments[indexOfAppIdSegment];
        const urlToRedirectTo = `/admin/app-details/${appId}`;
        this._router.navigateByUrl(urlToRedirectTo);
      }
    }
  };

  private splitQueryParam = (queryParamNameAndValue: string) => {
    let index = 0;
    for (index; index < queryParamNameAndValue.length; index++) {
      if (queryParamNameAndValue.charAt(index) === '=') {
        break;
      }
    }
    const paramName = queryParamNameAndValue.slice(0, index);
    const paramValue = queryParamNameAndValue.slice(index + 1, queryParamNameAndValue.length);
    return {
      paramName,
      paramValue
    }
  }

  private properlyEncodeUrl = (url: string) => {
    const baseUrlAndQuery = url.split('?');
    if (baseUrlAndQuery.length === 1) {
      return url;
    }
    const queryStringParts = baseUrlAndQuery[1].split('&');
    let queryString = '';
    queryStringParts.forEach((part) => {
      const nameAndValue = this.splitQueryParam(part);
      let queryStringSegment = '';
      if (nameAndValue.paramName && nameAndValue.paramValue) {
        queryStringSegment =
          nameAndValue.paramName + '=' + encodeURIComponent(nameAndValue.paramValue);
        if (queryStringSegment) {
          queryString += '&' + queryStringSegment;
        }
      }
    });
    if (queryString) {
      queryString = queryString.substring(1);
    }
    return baseUrlAndQuery[0] + '?' + queryString;
  };
}
